'use client'

import { useEffect, useRef } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import {
  installScriptBoundaryGuard,
  reloadIfCarryingForbiddenScript,
  type LeftPage,
} from './script-boundary'
import { ANALYTICS_READY_EVENT } from './track'

/**
 * Mounts the link-click and back/forward guard from `script-boundary.ts` once
 * for the whole site, keeps it told which page is being left, and after every
 * navigation (and whenever a loader installs) reloads a page that ended up
 * carrying a script it forbids.
 *
 * Uses `useSearchParams`, so the caller wraps this in `<Suspense>`.
 */
export function ScriptBoundaryGuard() {
  const pathname = usePathname()
  const query = useSearchParams()?.toString() ?? ''
  const leftPage = useRef<LeftPage>({ url: '', state: null })

  useEffect(() => {
    // A navigation the guard did not catch still must not keep a forbidden script.
    if (reloadIfCarryingForbiddenScript()) return
    leftPage.current = { url: window.location.href, state: window.history.state }
  }, [pathname, query])

  useEffect(() => installScriptBoundaryGuard(() => leftPage.current), [])

  // A loader that installs AFTER the navigation (Clarity is `lazyOnload`) is
  // checked when it announces itself, not only at the next navigation.
  useEffect(() => {
    const recheck = () => {
      reloadIfCarryingForbiddenScript()
    }
    window.addEventListener(ANALYTICS_READY_EVENT, recheck)
    return () => window.removeEventListener(ANALYTICS_READY_EVENT, recheck)
  }, [])

  return null
}
